import React from 'react';
import { Box } from '@mui/material';
import { SparkTabs, SparkButton, InfoCard } from 'sharedComponents/UI';
import type { StepTest } from 'types/SparkStep';
import SparkTestsOutput from './SparkTestsOutput';
import SparkTestsInput from './SparkTestsInput';
import { useSparkBuilderStore } from 'stores';
import { useSparkStepByID } from 'hooks/SparkBuilder/sparkStep';
import { formatDateAndTime } from 'utilities/time';
import type { SingleSparkTaskResponse } from 'types/SparkTasks';
import SparkPromptTestInput from './SparkPromptTestInput';
import { STEP_TYPE_NAMES } from 'constants/SparkSteps';
import { useGetPromptFunctionsByID } from 'hooks/SparkBuilder/promptFunctions';

type SparkTestsTabsProps = {
  handleTestClick: () => Promise<void>;
  isParentLoading?: boolean;
  addStepError: Error;
  onContinue: () => void;
  addStepData?: SingleSparkTaskResponse | undefined;
  sparkTestResults: StepTest | null;
  outputTypeID?: string | null;
  isAddTestRecordLoading?: boolean;
};

function SparkTestsTabs({
  handleTestClick,
  isParentLoading = false,
  onContinue,
  addStepError,
  addStepData,
  sparkTestResults,
  outputTypeID,
  isAddTestRecordLoading = false,
}: SparkTestsTabsProps) {
  const selectedStep = useSparkBuilderStore(
    React.useCallback((store) => store.builderSideBar.selectedStep, [])
  );
  const [outputError, setOutputError] = React.useState<string | null>(null);
  console.log('outputError', outputError);
  const { data: sparkStep } = useSparkStepByID(selectedStep?.id);
  const { data: promptFunction } = useGetPromptFunctionsByID(
    sparkStep?.data.attributes.promptFunctionID
  );
  const hasOutput = sparkTestResults?.SparkTask.Output;
  const hasInput =
    !!sparkStep?.data.meta.inputResolved ||
    !!promptFunction?.data?.attributes?.prompt;

  const [tabIndex, setTabIndex] = React.useState(
    hasOutput || isAddTestRecordLoading ? 1 : 0
  );

  const hasReferenceIssues =
    sparkStep?.data?.meta?.diagnostics?.hasReferenceIssues;

  const hasTaskError =
    addStepData?.data?.attributes?.sparkTaskStatusTypeID === 4;

  const SparkTaskErrorDescription =
    addStepData?.data?.attributes?.statusDescription;

  const sparkStepTypeIdMap = useSparkBuilderStore(
    (store) => store.stepTypeIdMap
  );
  const functionType =
    sparkStepTypeIdMap?.[sparkStep?.data?.attributes?.sparkStepTypeID || ''];
  const functionName = functionType?.attributes.key || '';

  const warning = 'Fix reference issues first';

  React.useEffect(() => {
    if (hasTaskError) {
      setTabIndex(0);
    }
  }, [hasTaskError]);

  const tabItems = React.useMemo(() => {
    const items = [
      {
        label: 'Input',
        content:
          functionName === STEP_TYPE_NAMES.PROMPT || STEP_TYPE_NAMES.MEMORY ? (
            <SparkPromptTestInput />
          ) : (
            <SparkTestsInput isParentLoading={isParentLoading} />
          ),
      },
    ];
    if (hasOutput || isAddTestRecordLoading) {
      items.push({
        label: 'Output',
        content: (
          <SparkTestsOutput
            isParentLoading={isParentLoading || isAddTestRecordLoading}
            sparkTestResults={sparkTestResults}
            outputTypeID={outputTypeID}
            onError={setOutputError}
            outputError={outputError}
          />
        ),
      });
    }
    return items;
  }, [
    functionName,
    hasOutput,
    isAddTestRecordLoading,
    isParentLoading,
    outputError,
    outputTypeID,
    sparkTestResults,
  ]);

  const handleTest = async () => {
    setOutputError(null);
    try {
      setTabIndex(1);
      await handleTestClick();
      setTabIndex(1);
    } catch (error) {
      setTabIndex(0);
    }
  };
  return (
    <>
      <InfoPanel
        addStepError={addStepError}
        sparkTestResults={sparkTestResults}
        hasTaskError={hasTaskError}
        sparkTaskErrorDescription={SparkTaskErrorDescription}
        outputError={outputError}
      />
      <SparkTabs
        selectedItemIndex={tabIndex}
        tabItems={tabItems}
        onChange={(value) => {
          setTabIndex(value);
        }}
        tabPanelStyles={{ paddingY: 0 }}
        tabNavigationContainerStyles={{ marginX: 2 }}
      />
      <Box paddingX={2}>
        {!hasOutput ? (
          <SparkButton
            fullWidth
            onClick={handleTest}
            variant='contained'
            sx={{
              paddingY: 1.5,
              paddingX: 3,
            }}
            disabled={isParentLoading || hasReferenceIssues || !hasInput}
          >
            Test
          </SparkButton>
        ) : (
          <>
            {hasReferenceIssues && warning && (
              <Box marginBottom={2}>
                <InfoCard info={warning} type={'warning'} />
              </Box>
            )}
            <Box
              display='flex'
              justifyContent='space-between'
              width='100%'
              gap={2}
            >
              <SparkButton
                fullWidth
                onClick={handleTest}
                variant='outlined'
                sx={{
                  paddingY: 1.5,
                  paddingX: 3,
                }}
                disabled={isParentLoading || hasReferenceIssues || !hasInput}
              >
                Retest Step
              </SparkButton>
              <SparkButton
                fullWidth
                onClick={onContinue}
                variant='contained'
                sx={{
                  paddingY: 1.5,
                  paddingX: 3,
                }}
                disabled={isParentLoading || hasReferenceIssues || !hasInput}
              >
                Continue
              </SparkButton>
            </Box>
          </>
        )}
      </Box>
    </>
  );
}

export default SparkTestsTabs;

function InfoPanel({
  sparkTestResults,
  addStepError,
  hasTaskError,
  sparkTaskErrorDescription,
  outputError,
}: {
  addStepError: Error;
  sparkTestResults: StepTest | null | undefined;
  hasTaskError: boolean;
  sparkTaskErrorDescription: string | undefined;
  outputError: string | null;
}) {
  const { LastTested } = sparkTestResults ?? {};

  const getCardType = (): 'error' | 'info' | 'warning' => {
    if (outputError) return 'error';
    if (addStepError || hasTaskError) return 'error';
    if (LastTested) return 'info';
    return 'warning';
  };

  const getInfoMessage = (): string => {
    if (outputError) return outputError;
    if (addStepError) return addStepError.message;
    if (hasTaskError)
      return (
        sparkTaskErrorDescription ?? 'An error occurred while testing this step'
      );
    if (LastTested)
      return `Tested on ${formatDateAndTime(
        LastTested,
        'DD MMM YYYY HH:mm:ss'
      )}`;
    return 'This step has not been tested';
  };

  return (
    <Box padding={2} pb={1}>
      <InfoCard info={getInfoMessage()} type={getCardType()} />
    </Box>
  );
}
